import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { debugLogger } from '@/utils/debugLogger';
import type { WheelPrizeConfig } from '@/types/gems';

export interface WheelAdminStats {
  totalSpins: number;
  freeSpins: number;
  paidSpins: number;
  uniqueUsers: number;
  totalGemsSpent: number;
  totalGemsWon: number;
  totalBoostsGiven: number;
  totalVipDaysGiven: number;
  profitGems: number;
  profitBRL: number;
}

export interface WheelSpinLog {
  id: string;
  username: string;
  prizeType: string;
  prizeValue: number;
  prizeEmoji: string;
  prizeLabel: string;
  wasFree: boolean;
  gemsSpent: number;
  createdAt: string;
}

const GEM_VALUE_BRL = 0.50; // Valor estimado de 1 gema em R$

export const useAdminWheelData = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<WheelAdminStats>({
    totalSpins: 0,
    freeSpins: 0,
    paidSpins: 0,
    uniqueUsers: 0,
    totalGemsSpent: 0,
    totalGemsWon: 0,
    totalBoostsGiven: 0,
    totalVipDaysGiven: 0,
    profitGems: 0,
    profitBRL: 0,
  });
  const [prizes, setPrizes] = useState<WheelPrizeConfig[]>([]);
  const [spinHistory, setSpinHistory] = useState<WheelSpinLog[]>([]);
  const [savingPrizes, setSavingPrizes] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      // Buscar histórico de giros
      const { data: spins, error } = await supabase
        .from('wheel_spin_history')
        .select('*');

      if (error) throw error;

      if (!spins || spins.length === 0) {
        setStats({
          totalSpins: 0,
          freeSpins: 0,
          paidSpins: 0,
          uniqueUsers: 0,
          totalGemsSpent: 0,
          totalGemsWon: 0,
          totalBoostsGiven: 0,
          totalVipDaysGiven: 0,
          profitGems: 0,
          profitBRL: 0,
        });
        return;
      }

      const totalSpins = spins.length;
      const freeSpins = spins.filter(s => s.was_free_spin).length;
      const paidSpins = spins.filter(s => !s.was_free_spin).length;
      const uniqueUsers = new Set(spins.map(s => s.user_id)).size;
      const totalGemsSpent = spins.reduce((sum, s) => sum + (s.gems_spent || 0), 0);
      
      // Calcular gemas ganhas
      const totalGemsWon = spins
        .filter(s => s.prize_type === 'gems')
        .reduce((sum, s) => sum + (s.prize_value || 0), 0);

      // Calcular boosts dados
      const totalBoostsGiven = spins
        .filter(s => s.prize_type === 'boost')
        .reduce((sum, s) => sum + (s.prize_value || 0), 0);

      // Calcular dias VIP dados
      const totalVipDaysGiven = spins
        .filter(s => s.prize_type === 'vip_day')
        .reduce((sum, s) => sum + (s.prize_value || 0), 0);

      // Lucro = gemas recebidas - gemas distribuídas
      const profitGems = totalGemsSpent - totalGemsWon;
      const profitBRL = profitGems * GEM_VALUE_BRL;

      setStats({
        totalSpins,
        freeSpins,
        paidSpins,
        uniqueUsers,
        totalGemsSpent,
        totalGemsWon,
        totalBoostsGiven,
        totalVipDaysGiven,
        profitGems,
        profitBRL,
      });
    } catch (error) {
      debugLogger.error('AdminWheel', 'Error fetching stats:', error);
    }
  }, []);

  const fetchPrizes = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('wheel_prizes_config')
        .select('*')
        .order('slot_number', { ascending: true });

      if (error) throw error;
      setPrizes((data || []) as WheelPrizeConfig[]);
    } catch (error) {
      debugLogger.error('AdminWheel', 'Error fetching prizes:', error);
    }
  }, []);

  const fetchSpinHistory = useCallback(async (limit = 50) => {
    try {
      const { data: spins, error } = await supabase
        .from('wheel_spin_history')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      // Buscar usernames
      const userIds = [...new Set((spins || []).map(s => s.user_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username')
        .in('id', userIds);

      const usernameMap = new Map(profiles?.map(p => [p.id, p.username]) || []);

      // Buscar config de prêmios para labels e emojis
      const { data: prizeConfigs } = await supabase
        .from('wheel_prizes_config')
        .select('slot_number, display_label, display_emoji');

      const prizeMap = new Map<number, { label: string; emoji: string }>(
        prizeConfigs?.map(p => [p.slot_number, { label: p.display_label, emoji: p.display_emoji }]) || []
      );

      const logs: WheelSpinLog[] = (spins || []).map(spin => {
        const prizeInfo = prizeMap.get(spin.prize_slot) || { label: 'Prêmio', emoji: '🎁' };
        return {
          id: spin.id,
          username: usernameMap.get(spin.user_id) || 'Desconhecido',
          prizeType: spin.prize_type,
          prizeValue: spin.prize_value,
          prizeEmoji: prizeInfo.emoji,
          prizeLabel: prizeInfo.label,
          wasFree: spin.was_free_spin,
          gemsSpent: spin.gems_spent,
          createdAt: spin.created_at,
        };
      });

      setSpinHistory(logs);
    } catch (error) {
      debugLogger.error('AdminWheel', 'Error fetching spin history:', error);
    }
  }, []);

  const updatePrizeProbabilities = useCallback(async (updatedPrizes: WheelPrizeConfig[]) => {
    setSavingPrizes(true);
    try {
      // Validar que soma é ~100%
      const total = updatedPrizes.reduce((sum, p) => sum + p.probability, 0);
      if (Math.abs(total - 1) > 0.001) {
        toast.error('A soma das probabilidades deve ser 100%');
        return false;
      }

      // Atualizar cada prêmio
      for (const prize of updatedPrizes) {
        const { error } = await supabase
          .from('wheel_prizes_config')
          .update({ probability: prize.probability })
          .eq('id', prize.id);

        if (error) throw error;
      }

      setPrizes(updatedPrizes);
      toast.success('Probabilidades atualizadas com sucesso!');
      return true;
    } catch (error) {
      debugLogger.error('AdminWheel', 'Error updating prizes:', error);
      toast.error('Erro ao atualizar probabilidades');
      return false;
    } finally {
      setSavingPrizes(false);
    }
  }, []);

  const refreshData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchStats(), fetchPrizes(), fetchSpinHistory()]);
    setLoading(false);
  }, [fetchStats, fetchPrizes, fetchSpinHistory]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  return {
    loading,
    stats,
    prizes,
    spinHistory,
    savingPrizes,
    updatePrizeProbabilities,
    refreshData,
  };
};
